import {Inject, Injectable} from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import {AuthService} from './auth.service';
import {Router} from '@angular/router';
import {tap} from 'rxjs/operators';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Observable} from 'rxjs';
import {resolveTenantApiUrl} from '../../shared/http/tenant-api-url';
import {HttpAppError, mapToHttpAppError} from '../../shared/http/http-app-error';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  constructor(
    public auth: AuthService,
    private router: Router,
    @Inject('BASE_API_URL') private baseUrl: string,
    public snackBar: MatSnackBar) {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const url = resolveTenantApiUrl(this.auth.getBaseUrl(), request.url);

    if (!request.headers.has('Authorization')) {
      if (this.shouldAttachAuthorization(url)) {
        request = request.clone({
          url,
          setHeaders: {
            Authorization: `Bearer ${this.auth.getToken()}`
          }
        });
      } else {
        if (this.auth.getToken()) {
          console.warn(
            'Skipping Authorization: request host does not match token issuer',
            {url, issuer: this.auth.getTokenIssuer()}
          );
        }
        request = request.clone({url});
      }
    } else {
      request = request.clone({
        url
      });
    }

    return next.handle(request)
      .pipe(
        tap(() => {
        }, (err: any) => {
          const appErr = mapToHttpAppError(err);
          if (appErr) {
            this.handleAppError(appErr);
          }
        })
      );
  }

  private handleAppError(err: HttpAppError): void {
    if (err.isUnauthorized) {
      // Single coordinated logout — parallel 401s share AuthService.handleUnauthorized (AC-24).
      this.auth.handleUnauthorized(this.router.url);
      return;
    }
    if (err.isForbidden) {
      this.router.navigate(['modules'], {queryParams: {returnUrl: this.router.url}});
      return;
    }
    this.snackBar.open(err.message || 'אירעה שגיאה במהלך טעינת הנתונים', '', {
      duration: 2000,
    });
  }

  /** Attach Bearer token only when the request targets the token issuer host (or same-origin /api). */
  shouldAttachAuthorization(requestUrl: string): boolean {
    const token = this.auth.getToken();
    if (!token) {
      return false;
    }
    const issuer = this.auth.getTokenIssuer() || this.auth.getBaseUrl() || '';
    if (!issuer) {
      // Dev / relative `/api/...` on the SPA origin — attach when we have a token.
      try {
        const req = new URL(requestUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return typeof window !== 'undefined'
          && req.origin === window.location.origin
          && req.pathname.startsWith('/api/');
      } catch {
        return false;
      }
    }
    return TokenInterceptor.hostsMatch(issuer, requestUrl);
  }

  static hostsMatch(issuerBase: string, requestUrl: string): boolean {
    try {
      const issuerUrl = new URL(issuerBase, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      const reqUrl = new URL(requestUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      const issuerPort = issuerUrl.port || (issuerUrl.protocol === 'https:' ? '443' : '80');
      const reqPort = reqUrl.port || (reqUrl.protocol === 'https:' ? '443' : '80');
      return issuerUrl.hostname.toLowerCase() === reqUrl.hostname.toLowerCase()
        && issuerPort === reqPort;
    } catch {
      return false;
    }
  }
}
